import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ContinueReading } from "@/components/textbooks/ContinueReading";
import { BookOpen, TrendingUp, CheckCircle2, XCircle, ArrowRight, Timer } from "lucide-react";

function StatCard({ label, value, tone = "indigo", testid, icon: Icon }) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="qs-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{label}</p>
          <p className="font-display text-3xl font-black text-slate-900 mt-2">{value}</p>
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-xl grid place-items-center ${tones[tone]}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/exams").then((r) => setExams(r.data)),
      api.get("/student/analytics").then((r) => setAnalytics(r.data)),
      api.get("/attempts/mine").then((r) => setAttempts(r.data)),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const completedExamIds = new Set(
    attempts.filter((a) => a.status === "submitted").map((a) => a.exam_id)
  );
  const pendingExams = exams.filter((e) => !completedExamIds.has(e.id));
  const recentAttempts = attempts.slice(0, 5);

  return (
    <AppShell title={`Welcome, ${user?.name?.split(" ")[0] || ""} 👋`} subtitle="Your learning snapshot for today.">
      {loading ? (
        <div className="text-slate-500 text-sm" data-testid="dashboard-loading">Loading…</div>
      ) : (
        <div className="space-y-6" data-testid="student-dashboard">
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Exams Available" value={pendingExams.length} icon={BookOpen} tone="indigo" testid="stat-available" />
            <StatCard label="Attempts" value={analytics?.total_attempts || 0} icon={Timer} tone="amber" testid="stat-attempts" />
            <StatCard label="Avg. Score" value={`${analytics?.average_score ?? 0}%`} icon={TrendingUp} tone="emerald" testid="stat-avg" />
            <StatCard label="Passed" value={analytics?.passed || 0} icon={CheckCircle2} tone="emerald" testid="stat-passed" />
          </div>

          <ContinueReading />

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Available exams */}
            <div className="lg:col-span-2 qs-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900">Ready to take</h3>
                  <p className="text-sm text-slate-500">Published exams waiting for you.</p>
                </div>
                <Link to="/dashboard/exams" className="text-sm font-semibold text-indigo-600 hover:underline" data-testid="link-all-exams">
                  See all
                </Link>
              </div>
              <div className="mt-4 divide-y divide-slate-100">
                {pendingExams.slice(0, 5).map((e) => (
                  <div key={e.id} className="py-3 flex items-center gap-4" data-testid={`exam-row-${e.id}`}>
                    <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 grid place-items-center">
                      <BookOpen className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{e.title}</p>
                      <p className="text-xs text-slate-500">
                        {e.subject} · {e.duration_min} min · {e.questions?.length || 0} questions
                      </p>
                    </div>
                    <Link to={`/exam/${e.id}`} className="btn-primary text-xs" data-testid={`start-exam-${e.id}`}>
                      Start <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                ))}
                {pendingExams.length === 0 && (
                  <p className="text-sm text-slate-500 py-6 text-center" data-testid="no-exams">
                    No pending exams &mdash; you&rsquo;re all caught up!
                  </p>
                )}
              </div>
            </div>

            {/* Recent results */}
            <div className="qs-card p-6">
              <h3 className="font-display text-lg font-bold text-slate-900">Recent results</h3>
              <p className="text-sm text-slate-500">Your last few attempts.</p>
              <div className="mt-4 space-y-3">
                {recentAttempts.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-6" data-testid="no-attempts">
                    Take your first exam to see results here.
                  </p>
                )}
                {recentAttempts.map((a) => (
                  <Link
                    key={a.id}
                    to={`/dashboard/attempts/${a.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition"
                    data-testid={`attempt-row-${a.id}`}
                  >
                    <div className={`w-9 h-9 rounded-lg grid place-items-center ${a.passed ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                      {a.passed ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-slate-800 truncate">{a.exam_title}</p>
                      <p className="text-xs text-slate-500">{a.score_percent}% · {a.status}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Subject breakdown */}
          {analytics?.by_subject?.length > 0 && (
            <div className="qs-card p-6" data-testid="subject-breakdown">
              <h3 className="font-display text-lg font-bold text-slate-900">Performance by subject</h3>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {analytics.by_subject.map((s) => (
                  <div key={s.subject} className="p-4 rounded-xl border border-slate-100 bg-slate-50/60">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-slate-800">{s.subject}</p>
                      <p className="text-sm font-bold text-indigo-600">{s.average}%</p>
                    </div>
                    <div className="qs-progress mt-3">
                      <span style={{ width: `${Math.min(100, s.average)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
